import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Person,
  ExitToApp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
  DoNotDisturbOn,
  ApartmentRounded,
  ShieldRounded,
  TuneRounded,
} from '@mui/icons-material';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { updateUserPresence, setUserStatus, logOut, selectUserStatus, updateChatStatus } from '../../redux/user/user.actions';
import { useWebSocket } from '../../components/WebSocketContext/WebSocketContext';
import '../UserStatus/user-status.css';

const getStatusClass = (status) => {
  switch (status) {
    case 1: return 'online';
    case 2: return 'online';
    case 3: return 'offline';
    default: return 'offline';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 1:
    case 2: return 'Ativo';
    case 3: return 'Inativo';
    default: return 'Status';
  }
};

/**
 * Rotulos dos papeis padrao, para quando o perfil nao vier carregado.
 *
 * A resposta do login traz `profile` inteiro, mas uma sessao antiga guardada
 * no localStorage pode ter sido escrita antes disso -- e ai so sobra
 * `profile_role`. E rede, nao o caminho normal.
 */
const PAPEIS = {
  admin: 'Administrador',
  leader: 'Líder',
  operator: 'Operador',
};

/**
 * Um campo do cartao de identidade: rotulo pequeno em cima, valor embaixo.
 *
 * ## Por que rotulo em cima, e nao "Departamento: Suporte" numa linha
 *
 * O valor e o que se le; o rotulo so existe para dizer o que aquele valor e.
 * Com o rotulo em caixa menor e cinza por cima, o olho pega os dois valores
 * de uma vez, em coluna -- e um nome comprido corta com reticencias em vez de
 * empurrar o campo seguinte para baixo.
 *
 * ## Por que e uma peca propria
 *
 * Porque sao dois campos com o MESMO tratamento, e o que importa e eles
 * continuarem iguais: se um dia o departamento ganhar um icone colorido que o
 * perfil nao tenha, os dois param de se ler como um par e viram dois avisos
 * soltos no topo do menu.
 */
const CampoDoOperador = ({ icone, rotulo, valor, naoDefinido }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        color: 'text.secondary',
      }}
    >
      {icone}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          lineHeight: 1.2,
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {rotulo}
      </Typography>
      {/* O `title` serve o nome inteiro quando ele nao cabe e vira reticencias. */}
      {/*
        * O testid sai do PROPRIO rotulo, e nao de uma string a parte: assim
        * nao existe estado em que o teste procura o valor de um campo e
        * encontra o de outro, que e exatamente o erro que uma peca de rotulo
        * + valor pode cometer sem parecer quebrada.
        */}
      <Typography
        variant="body2"
        data-testid={`campo-${rotulo.toLowerCase()}`}
        title={valor}
        sx={{
          lineHeight: 1.3,
          fontWeight: 500,
          color: naoDefinido ? 'text.disabled' : 'text.primary',
          fontStyle: naoDefinido ? 'italic' : 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {valor}
      </Typography>
    </Box>
  </Box>
);

/*
 * O item do menu, nos tres lugares em que ele aparece.
 *
 * Cantos arredondados com margem lateral: assim o realce do hover e uma
 * pastilha dentro do cartao, e nao uma faixa que bate nas duas bordas do
 * menu -- que e o que fazia o menu antigo parecer uma lista de sistema.
 */
const estiloDoItem = {
  mx: 0.75,
  borderRadius: 1.5,
  minHeight: 40,
};

const PRESENCE_PAUSE_PERMISSION = '#pausar-status-online-operadores';

const ProfileMenu = ({ currentUser, permissions, updateUserPresence, logOut, size = 'medium', ...props }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusExpanded, setStatusExpanded] = useState(false);
  const { updateUserStatus } = useWebSocket();
  const userStatus = useSelector(selectUserStatus);
  const dispatch = useDispatch();
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const effectiveUser = currentUser || user;

  const canChangePresence = useMemo(() => {
    const list = Array.isArray(permissions) ? permissions : [];
    return (
      list.includes(PRESENCE_PAUSE_PERMISSION) ||
      Number(effectiveUser?.profile_id) === 1 ||
      Boolean(effectiveUser?.is_super_admin)
    );
  }, [permissions, effectiveUser?.profile_id, effectiveUser?.is_super_admin]);

  const currentStatus = userStatus?.status ?? effectiveUser?.status ?? 3;

  /*
   * "Departamento" aqui e o GRUPO DE ATENDIMENTO, e isso nao e atalho.
   *
   * A tabela `departments` existiu e foi derrubada em 2023 (migration
   * 2023_11_04_124605_delete_selected_tables da API); o que ficou no lugar foi
   * `service_groups`. E "departamento" e como o resto do produto ja chama
   * esse dado: a consulta de cadastro rotula `grupo_atendimento` como
   * "Departamento", e o chat fala em "transferir para departamento". Usar a
   * palavra da equipe apontando para o dado que existe e o que mantem esta
   * peca coerente com as outras telas -- inventar um campo novo, nao.
   */
  const departamento = effectiveUser?.service_group?.description;

  /*
   * O perfil vem pelo NOME, nao pelo papel.
   *
   * Cada tenant tem os proprios perfis desde a migration 2026_08_29_120000,
   * entao o nome e o que o administrador daquela empresa escolheu e
   * reconhece -- "Supervisao de Farmacia" diz mais do que "leader".
   * `profile_role` so entra como rede, para sessao antiga no localStorage.
   */
  const perfil = effectiveUser?.profile?.name || PAPEIS[effectiveUser?.profile_role];

  useEffect(() => {
    if (effectiveUser?.id != null) {
      dispatch(setUserStatus({ userId: effectiveUser.id, status: effectiveUser.status }));
    }
  }, [effectiveUser?.id, effectiveUser?.status, dispatch]);

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setStatusExpanded(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setStatusExpanded(false);
  };

  const handleViewProfile = () => {
    handleClose();
    history.push('/perfil');
  };

  const handleStatusChange = (status) => (event) => {
    event.stopPropagation();
    if (!canChangePresence) return;
    dispatch(setUserStatus({ userId: effectiveUser?.id, status }));
    updateUserPresence(effectiveUser?.id, status);
    updateUserStatus({ userId: effectiveUser?.id, status });
  };

  // Status de CHAT (0 Indisponível / 1 Disponível) — o próprio operador sempre pode.
  const chatStatus = Number(effectiveUser?.chat_status) || 0;
  const handleChatStatusChange = (value) => (event) => {
    event.stopPropagation();
    dispatch(updateChatStatus(effectiveUser?.id, value));
  };

  const handleLogout = () => {
    handleClose();
    logOut();
  };

  const avatarSize = size === 'small' ? 32 : 36;

  /*
   * O status de chat, FORA do avatar (15/10/2026).
   *
   * ## O problema que ele resolve
   *
   * Havia duas marcas sobre o avatar -- presença no sistema em cima,
   * disponibilidade no chat embaixo --, do mesmo tamanho e no mesmo lugar,
   * codificando eixos diferentes. Nada dizia qual era qual: só o tooltip
   * desempatava, e tooltip exige que a pessoa descubra que ele existe.
   *
   * Mover o ícone para fora resolveria a sobreposição e NÃO o "qual é qual" --
   * viraria um ícone solto que também precisa de tooltip. O texto é o que
   * torna a peça auto-explicativa, e o espaço existe: este cabeçalho tem só
   * notificações e avatar.
   *
   * E é o status de CHAT que merece o espaço, não o de sistema: é ele que
   * decide se a pessoa recebe conversa. O de sistema é informação ambiente, e
   * ambiente cabe na bolinha que ficou no avatar.
   *
   * ## Clicar ALTERNA, e não abre menu
   *
   * Decisão dele em 15/10. É o atalho de uma ação só que o operador faz várias
   * vezes por dia -- voltar de um almoço, sair para uma reunião. Abrir menu
   * para isso custaria três cliques onde um basta, e o menu do avatar continua
   * existindo, intacto, para quem quiser o caminho completo.
   *
   * Não pede confirmação: a ação é reversível no mesmo clique, e um diálogo
   * para alternar um botão de duas posições seria atrito sem ganho.
   *
   * ## A cor
   *
   * Verde e vermelho, aparecendo três vezes -- ícone, fundo translúcido e
   * borda -- sobre o azul fixo do cabeçalho. Texto branco, porque aquele fundo
   * é escuro nos dois temas.
   */
  const corDoChat = chatStatus === 1 ? '#4CAF50' : '#EF5350';

  const alternarChat = (event) => {
    event.stopPropagation();
    dispatch(updateChatStatus(effectiveUser?.id, chatStatus === 1 ? 0 : 1));
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/*
        * O tooltip anuncia o que o clique VAI FAZER, e não o estado atual --
        * o estado já está escrito na pílula, em letra legível. "Status no
        * chat: Disponível" seria repetir o que se lê e esconder que ali se
        * clica.
        */}
      <Tooltip
        title={chatStatus === 1 ? 'Clique para ficar indisponível' : 'Clique para ficar disponível'}
        arrow
        placement="bottom"
      >
        <Box
          component="button"
          type="button"
          onClick={alternarChat}
          data-testid="chip-status-do-chat"
          aria-pressed={chatStatus === 1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
            fontSize: '0.75rem',
            color: '#fff',
            backgroundColor: alpha(corDoChat, 0.18),
            border: '1px solid',
            borderColor: alpha(corDoChat, 0.45),
            transition: 'background-color 0.2s',
            '&:hover': { backgroundColor: alpha(corDoChat, 0.3) },
          }}
        >
          {chatStatus === 1
            ? <CheckCircle sx={{ fontSize: 15, color: corDoChat }} />
            : <DoNotDisturbOn sx={{ fontSize: 15, color: corDoChat }} />}
          {chatStatus === 1 ? 'Disponível' : 'Indisponível'}
        </Box>
      </Tooltip>
      <Tooltip title={user?.name || user?.email || 'Perfil'} arrow placement="bottom">
        <IconButton
          size={size}
          onClick={handleOpen}
          sx={{
            color: 'white',
            p: 0.5,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              transform: 'scale(1.05)',
            },
          }}
        >
          <Box sx={{ position: 'relative', width: avatarSize, height: avatarSize }}>
            {effectiveUser?.picture ? (
              <Avatar
                src={effectiveUser.picture}
                alt={effectiveUser?.name}
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  border: '2px solid rgba(255,255,255,0.9)',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.9rem',
                  border: '2px solid rgba(255,255,255,0.9)',
                }}
              >
                {(effectiveUser?.name || effectiveUser?.email || '?')[0].toUpperCase()}
              </Avatar>
            )}
            <Tooltip title={`Sistema: ${getStatusText(currentStatus)}`} arrow placement="right">
              <Box
                className={`status-icon ${getStatusClass(currentStatus)}`}
                sx={{
                  pointerEvents: 'none',
                  top: 0,
                  bottom: 'auto',
                  left: 'calc(50% + 10px)',
                  right: 'auto',
                  transform: 'translateX(-50%)',
                  width: 10,
                  height: 10,
                  borderWidth: 2,
                }}
              />
            </Tooltip>
            {/*
              * A marca do status de CHAT saiu DAQUI em 15/10/2026.
              *
              * Ela ficava embaixo do avatar, ao lado da de sistema, e as duas
              * juntas eram o problema: mesmo tamanho, mesmo lugar, eixos
              * diferentes e nenhum rotulo. O chat virou a pilula a esquerda do
              * avatar, que diz a palavra e alterna no clique.
              *
              * A de SISTEMA fica -- ela e informacao ambiente, e sozinha nao
              * disputa leitura com ninguem.
              */}
          </Box>
        </IconButton>
      </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          /*
            * O minimo de 250 veio de "Indisponível" caber numa linha so na
            * pilula do status: com 220 a palavra QUEBRAVA EM DUAS LINHAS e o
            * bloco do Chat ficava com 46px de altura contra 29px do Sistema,
            * e era isso que fazia os dois parecerem controles de telas
            * diferentes (medido em 15/10/2026).
            *
            * Subiu para 290 em 29/10/2026 por causa do cartao do topo: o
            * departamento e o unico campo daqui que pode ser longo de verdade
            * ("Suporte Tecnico - Farmacia Popular"), e com 250 ele cortava
            * quase sempre. O minimo antigo continua respeitado -- a largura so
            * cresceu.
            *
            * `elevation: 0` com borda e sombra proprias: a sombra padrao do
            * MUI num Paper de canto 20px deixa um halo que marca o retangulo
            * antigo por baixo do arredondamento.
            */
          sx: {
            minWidth: 290,
            mt: 1.5,
            borderRadius: 2.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08)',
          },
        }}
        /*
          * `py: 0` para o cartao de identidade encostar na borda de cima. O
          * respiro dos itens passou a ser margem DELES (`estiloDoItem`), que e
          * o que permite o hover virar pastilha em vez de faixa de ponta a
          * ponta.
          */
        MenuListProps={{ sx: { py: 0 } }}
      >
        {/*
          * O cartao de identidade, no topo do menu (29/10/2026).
          *
          * ## O que o menu nao dizia
          *
          * Ele abria direto em "Visualizar perfil" -- nem o nome de quem
          * estava logado aparecia aqui. Para conferir com que conta se estava,
          * em que departamento e com que perfil, era preciso navegar ate
          * /perfil e voltar.
          *
          * Departamento e perfil sao justamente os dois campos que mudam o que
          * a pessoa VE e PODE: a fila de onde o chat cai, e as permissoes da
          * tela. Sao o que se quer conferir antes de dizer que "nao esta
          * chegando chamado" ou que "nao tenho esse botao" -- e por isso ficam
          * a um clique, no mesmo lugar onde o avatar ja era clicado.
          *
          * ## Por que no menu, e nao no cabecalho
          *
          * O cabecalho e fixo e estreito, e ja carrega acessos rapidos, sino,
          * alertas, avisos, a pilula de status e o avatar. Departamento e
          * perfil quase nunca mudam: sao dado de conferencia, nao de operacao
          * -- pagam o espaco de um clique, nao o de uma barra permanente.
          */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1.75,
            backgroundImage: (t) =>
              `linear-gradient(135deg, ${alpha(
                t.palette.primary.main,
                t.palette.mode === 'dark' ? 0.24 : 0.1
              )} 0%, ${alpha(t.palette.primary.main, 0)} 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {effectiveUser?.picture ? (
              <Avatar
                src={effectiveUser.picture}
                alt={effectiveUser?.name}
                sx={{ width: 44, height: 44 }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '1.05rem',
                  fontWeight: 600,
                }}
              >
                {(effectiveUser?.name || effectiveUser?.email || '?')[0].toUpperCase()}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {effectiveUser?.name || 'Operador'}
              </Typography>
              <Typography
                variant="caption"
                title={effectiveUser?.email}
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {effectiveUser?.email}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.75 }}>
            <CampoDoOperador
              icone={<ApartmentRounded sx={{ fontSize: 16 }} />}
              rotulo="Departamento"
              valor={departamento || 'Não definido'}
              naoDefinido={!departamento}
            />
            <CampoDoOperador
              icone={<ShieldRounded sx={{ fontSize: 16 }} />}
              rotulo="Perfil"
              valor={perfil || 'Não definido'}
              naoDefinido={!perfil}
            />
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={handleViewProfile} sx={{ ...estiloDoItem, mt: 0.75 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>
            Visualizar perfil
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => setStatusExpanded(!statusExpanded)}
          sx={estiloDoItem}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <TuneRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Alterar status"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
          {statusExpanded ? (
            <KeyboardArrowUp fontSize="small" sx={{ color: 'text.secondary' }} />
          ) : (
            <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
          )}
        </MenuItem>
        <Collapse in={statusExpanded} timeout="auto" unmountOnExit>
          {/*
            * 22px, e nao os 16 de antes: e onde comeca o icone dos itens do
            * menu (6px da margem da pastilha + 16 do padding do MenuItem).
            * Com 16 os rotulos "Sistema" e "Chat" nasciam meio caractere a
            * esquerda da coluna de todo o resto -- de perto parece desalinho
            * de montagem, que e o tipo de detalhe que faz um menu parecer
            * remendado.
            */}
          <Box sx={{ pl: '22px', pr: '22px', pb: 1.5 }}>
            {/* Status no sistema (Ativo/Inativo) — só com permissão. */}
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
              Sistema
            </Typography>
            {canChangePresence ? (
              <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Box
                  onClick={handleStatusChange(1)}
                  sx={{
                    flex: 1, py: 0.75, px: 1.5, borderRadius: 0.75, textAlign: 'center', fontSize: '0.8125rem', whiteSpace: 'nowrap',
                    cursor: 'pointer', transition: 'all 0.2s',
                    bgcolor: currentStatus === 1 ? 'success.main' : 'transparent',
                    color: currentStatus === 1 ? 'success.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: currentStatus === 1 ? 'success.dark' : 'action.selected' },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: currentStatus === 1 ? 'inherit' : 'success.main', mr: 0.5, verticalAlign: 'middle' }} />
                  Ativo
                </Box>
                <Box
                  onClick={handleStatusChange(3)}
                  sx={{
                    flex: 1, py: 0.75, px: 1.5, borderRadius: 0.75, textAlign: 'center', fontSize: '0.8125rem', whiteSpace: 'nowrap',
                    cursor: 'pointer', transition: 'all 0.2s',
                    bgcolor: currentStatus === 3 ? 'error.main' : 'transparent',
                    color: currentStatus === 3 ? 'error.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: currentStatus === 3 ? 'error.dark' : 'action.selected' },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: currentStatus === 3 ? 'inherit' : 'error.main', mr: 0.5, verticalAlign: 'middle' }} />
                  Inativo
                </Box>
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Sem permissão para alterar.
              </Typography>
            )}

            {/* Status no chat (Disponível/Indisponível) — o próprio operador sempre pode. */}
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mt: 1.5, mb: 0.5 }}>
              Chat
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Box
                onClick={handleChatStatusChange(1)}
                sx={{
                  flex: 1, py: 0.75, px: 1.5, borderRadius: 0.75, textAlign: 'center', fontSize: '0.8125rem', whiteSpace: 'nowrap',
                  cursor: 'pointer', transition: 'all 0.2s',
                  bgcolor: chatStatus === 1 ? 'success.main' : 'transparent',
                  color: chatStatus === 1 ? 'success.contrastText' : 'text.secondary',
                  '&:hover': { bgcolor: chatStatus === 1 ? 'success.dark' : 'action.selected' },
                }}
              >
                <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: chatStatus === 1 ? 'inherit' : 'success.main', mr: 0.5, verticalAlign: 'middle' }} />
                Disponível
              </Box>
              {/*
                * "Indisponível" usava `grey.600` com a cor do texto CHUMBADA
                * em `#fff`, e a bolinha em `grey.500`. Ao lado do bloco de
                * Sistema -- que sai todo do tema (`error.main` /
                * `error.contrastText`) -- parecia um controle de outra tela:
                * cinza chapado onde o vizinho tem cor, e texto que não
                * acompanha tema nenhum.
                *
                * Agora é o mesmo tratamento do "Inativo" logo acima. Os dois
                * são o estado de "não estou recebendo", e passam a se parecer
                * -- que é o que o dono do produto pediu em 15/10/2026.
                */}
              <Box
                onClick={handleChatStatusChange(0)}
                sx={{
                  flex: 1, py: 0.75, px: 1.5, borderRadius: 0.75, textAlign: 'center', fontSize: '0.8125rem', whiteSpace: 'nowrap',
                  cursor: 'pointer', transition: 'all 0.2s',
                  bgcolor: chatStatus === 0 ? 'error.main' : 'transparent',
                  color: chatStatus === 0 ? 'error.contrastText' : 'text.secondary',
                  '&:hover': { bgcolor: chatStatus === 0 ? 'error.dark' : 'action.selected' },
                }}
              >
                <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: chatStatus === 0 ? 'inherit' : 'error.main', mr: 0.5, verticalAlign: 'middle' }} />
                Indisponível
              </Box>
            </Box>
          </Box>
        </Collapse>
        <Divider sx={{ mt: 0.75 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            ...estiloDoItem,
            mt: 0.75,
            mb: 0.75,
            color: 'error.main',
            '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' },
            '&:hover .MuiSvgIcon-root': { color: 'inherit' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ExitToApp fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>
            Sair
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.user?.currentUser,
  permissions: state.user?.permissions,
});

const mapDispatchToProps = (dispatch) => ({
  updateUserPresence: (userId, status) => dispatch(updateUserPresence(userId, status)),
  logOut: () => dispatch(logOut()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileMenu);
